import { FUEL, accraDay, conditionsFor, dailyChallenge, type ChallengeInfo, type Conditions } from '@accra/shared';
import type { PlayerVehicleId } from '../content/playerVehicles';
import { trafficDensity } from '../content/trafficLevels';
import { carriedFuel, type Profile } from '../meta/profile';

export type RunKind = 'normal' | 'daily' | 'friend';

export interface Rival {
  name: string;
  score: number;
}

/** Everything that decides how a shift is played: the road, the trotro, the random seed and who you're chasing. */
export interface RunPlan {
  kind: RunKind;
  seed: number;
  routeId: string;
  vehicle: PlayerVehicleId;
  /** Traffic multiplier (1 = normal). */
  traffic: number;
  /** Challenges are played without garage upgrades, so scores can be compared. */
  useUpgrades: boolean;
  rival: Rival | null;
  /** Weather and time of day, worked out from the seed so challenges are the same for everyone. */
  conditions: Conditions;
  /** Petrol in the tank at the start: whatever is left from your last shift (never under the minimum). Challenges start full for everyone. */
  fuelStart: number;
  /** What to tell the server when starting the shift. */
  start: { challenge?: 'daily'; friendCode?: string };
}

/**
 * Ordinary shifts use your own choices and a random seed. The daily challenge uses today's road, trotro and
 * seed (the same for everyone, no upgrades, normal traffic). A friend's challenge replays their road and trotro.
 */
export function planRun(kind: RunKind, profile: Profile, now: number, friend: ChallengeInfo | null, random: () => number = Math.random): RunPlan {
  if (kind === 'daily') {
    const today = dailyChallenge(accraDay(now));
    return { kind, seed: today.seed, routeId: today.route, vehicle: today.vehicle, traffic: 1, useUpgrades: false, rival: null, conditions: conditionsFor(today.seed), fuelStart: FUEL.tankUnits, start: { challenge: 'daily' } };
  }
  if (kind === 'friend' && friend) {
    return {
      kind, seed: friend.seed, routeId: friend.route ?? profile.routeId, vehicle: friend.vehicle as PlayerVehicleId, traffic: 1, useUpgrades: true,
      rival: { name: friend.name, score: friend.score }, conditions: conditionsFor(friend.seed), fuelStart: FUEL.tankUnits, start: { friendCode: friend.code },
    };
  }
  const seed = Math.floor(random() * 2 ** 31);
  return { kind: 'normal', seed, routeId: profile.routeId, vehicle: profile.vehicle, traffic: trafficDensity(profile.traffic), useUpgrades: true, rival: null, conditions: conditionsFor(seed), fuelStart: carriedFuel(profile.fuelUnits), start: {} };
}

/** "Chasing Kofi · 1,240 behind" / "Ahead of Kofi by 300". */
export function rivalLine(rival: Rival, score: number): { text: string; ahead: boolean } {
  const gap = Math.round(score) - rival.score;
  if (gap > 0) return { text: `Ahead of ${rival.name} by ${gap.toLocaleString()}`, ahead: true };
  return { text: `Chasing ${rival.name} · ${(-gap).toLocaleString()} behind`, ahead: false };
}
